import React, { useCallback, useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import {CATEGORIES, TIMEZONE} from "../constans";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs-timezone-iana-plugin";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

dayjs.extend(utc);
dayjs.extend(timezone);

/** Підкатегорія лише якщо є в довіднику для обраного типу. */
function resolvePresetCategoryValue(presetCategory, categoryTypeForList, isAdminUser) {
    if (!presetCategory || presetCategory === "Без категорії") {
        return "";
    }
    const listKey =
        categoryTypeForList === "personal" ? "personal" : "business";
    const effectiveKey = !isAdminUser ? "business" : listKey;
    const allowed = CATEGORIES[effectiveKey] || [];
    return allowed.includes(presetCategory) ? presetCategory : "";
}

const ManualTransactionModal = ({
    show,
    onClose,
    onSubmit,
    user,
    renderSubcategorySelect,
    /** Підгонка типу платежу/категорії під екран (/transactions/business|personal|cash). */
    presetCategoryType,
    presetPaymentType,
    /** Підкатегорія з контексту (наприклад query category=Пошив). */
    presetCategory,
    /** income | expense */
    presetDirection,
    /** YYYY-MM-DD; інакше сьогодні за TZ застосунку. */
    presetTimeYMD,
}) => {

    const isAdmin = user?.role === "admin";

    const makeFreshForm = useCallback(() => {
        const ctype = presetCategoryType ?? "business";
        return {
            amount: "",
            time:
                presetTimeYMD ??
                dayjs().tz(TIMEZONE).format("YYYY-MM-DD"),
            description: "",
            currency: "UAH",
            comment: "",
            category: resolvePresetCategoryValue(presetCategory, ctype, isAdmin),
            paymentType: presetPaymentType ?? "cash",
            categoryType: ctype,
            direction:
                presetDirection === "income" || presetDirection === "expense"
                    ? presetDirection
                    : "expense",
        };
    }, [
        presetCategory,
        presetCategoryType,
        presetDirection,
        presetPaymentType,
        presetTimeYMD,
        isAdmin,
    ]);

    const [form, setForm] = useState(() => makeFreshForm());

    useEffect(() => {
        if (show) {
            setForm(makeFreshForm());
        }
    }, [show, makeFreshForm]);

    function parseLocalDate(dateStr) {
        const [year, month, day] = dateStr.split("-").map(Number);

        // фиксируем время на 12:00, чтобы не было сдвигов
        return new Date(year, month - 1, day, 12, 0, 0);
    }

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "amount") {
            if (!/^\d*\.?\d{0,2}$/.test(value)) return; // только числа и максимум 2 знака после точки
        }

        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleClose = () => {
        setForm(makeFreshForm());
        onClose();
    };

    const handleSubmit = async () => {
        const { direction, categoryType } = form;

        const signedAmount =
            form.direction === "expense"
                ? -Math.abs(Math.round(+form.amount * 100))
                : Math.abs(Math.round(+form.amount * 100));

        const tx = {
            ...form,
            amount: signedAmount,
            categoryType: form.categoryType,
            time: parseLocalDate(form.time),
            isManual: true,
            cardType: form.paymentType,
            direction,
        };


        try {
            const res = await fetch("/api/transactions/manual", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include", // 🔥 ВАЖНО
                body: JSON.stringify(tx),
            });

            if (!res.ok) throw new Error("Помилка під час створення");

            const newTx = await res.json();
            onSubmit(newTx);
            setForm(makeFreshForm());
            onClose();
        } catch (err) {
            console.error("Помилка створення транзакції:", err);
            alert("Не вдалося створити транзакцію");
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Додати платіж вручну</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Сума</Form.Label>
                        <Form.Control
                            type="number"
                            name="amount"
                            value={form.amount}
                            onChange={handleChange}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Опис</Form.Label>
                        <Form.Control
                            type="text"
                            name="description"
                            value={form.description}
                            onChange={handleChange}
                            placeholder="Короткий опис платежу"
                            required
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Тип транзакції</Form.Label>
                        <Form.Select name="direction" value={form.direction} onChange={handleChange}>
                            <option value="expense">Витрата</option>
                            <option value="income">Доход</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Тип категорії</Form.Label>
                        <Form.Select
                            name="categoryType"
                            value={form.categoryType}
                            onChange={(e) => {
                                setForm((prev) => ({
                                    ...prev,
                                    categoryType: e.target.value,
                                    category: "",
                                }));
                            }}
                            disabled={!isAdmin}
                        >
                            <option value="business">Бізнес</option>
                            {isAdmin && <option value="personal">Особисте</option>}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Підкатегорія</Form.Label>
                        {typeof renderSubcategorySelect === "function" ? (
                            renderSubcategorySelect({ form, setForm, isAdmin })
                        ) : (
                        <Form.Select
                            name="category"
                            value={form.category || ""}
                            onChange={handleChange}
                        >
                            <option value="">Не вибрано</option>
                            {(CATEGORIES[
                                isAdmin ? form.categoryType : "business"
                                ] || []).map((cat) => (
                                <option key={cat} value={cat}>
                                    {cat}
                                </option>
                            ))}
                        </Form.Select>
                        )}
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Тип платежу</Form.Label>
                        <Form.Select
                            name="paymentType"
                            value={form.paymentType}
                            onChange={handleChange}
                        >
                            <option value="cash">Готівка</option>
                            <option value="non-cash">Безготівковий платіж</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Дата</Form.Label>
                        <DatePicker
                            selected={parseLocalDate(form.time)}
                            onChange={(date) => {
                                if (!date) return;
                                setForm((prev) => ({
                                    ...prev,
                                    time: dayjs(date).format("YYYY-MM-DD"),
                                }));
                            }}
                            locale="uk"
                            dateFormat="dd.MM.yyyy"
                            className="form-control"
                            wrapperClassName="w-100"
                            popperPlacement="bottom-start"
                            withPortal
                            portalId="datepicker-portal-root"
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            scrollableYearDropdown
                            yearDropdownItemNumber={30}
                            autoComplete="off"
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>Скасувати</Button>
                <Button variant="primary" onClick={handleSubmit}>Додати</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ManualTransactionModal;
